import {bearingBetween, nearestPointOnPath} from '@/utils/polyline'

/**
 * MAP MATCHING AGAINST THE ROAD NETWORK — the real one, not the route.
 *
 * ── What this is, and what it deliberately is not ─────────────────────────────────
 * The car used to be projected onto the SOLVED ROUTE within 60m. That put it on a road,
 * which was the goal, but on the road we had PLANNED rather than the road the driver was
 * on: the parallel street, the service road and the far carriageway are all inside 60m, so
 * a driver taking a shortcut was drawn gliding down the route they had just declined.
 *
 * This matches against every drivable road in the tiles instead. The nearest one is the
 * nearest one — if that is the shortcut, the car goes on the shortcut. The rule "the car is
 * always on a road" survives; the rule "the car is always on OUR road" does not, and it was
 * never a rule anyone asked for.
 *
 * ── Where the geometry comes from ─────────────────────────────────────────────────
 * `mapbox-streets-v8`, already loaded for the road labels and the buildings, queried
 * through the invisible `ROAD_SNAP_LAYER`. No request, no API, no key beyond the one the
 * map is already using: the roads on screen are in memory, and this reads them.
 *
 * ⚠️ IT IS A SCREEN QUERY, so it can only match roads the map has tiles for. The driving
 * camera keeps the car on screen, so in practice that is always true — but a match that
 * comes back null is a legitimate answer ("no road near, or no tiles yet"), and the caller
 * draws the raw fix. Failing to a raw position is the honest failure.
 */

/**
 * How far the car may be pulled to reach a road.
 *
 * ⚠️ MUCH TIGHTER THAN THE OLD 60m SNAP, and it can be, because this matches the road the
 * driver is actually on rather than the one we planned — the distance no longer has to
 * cover "which of these two roads did they take". 25m covers an ordinary urban fix's error
 * (5-20m) plus the offset of a wide carriageway's centreline from the lane being driven.
 *
 * ⚠️ 45m, RAISED FROM 25 AFTER A PHONE TEST DREW THE CAR INSIDE A HOUSE. 25 was chosen
 * against an urban fix's error on a good receiver; a phone indoors or between walls is
 * routinely 30-40m out, and every one of those fell past the threshold and was drawn raw —
 * which is the one thing this exists to prevent. The rule the portal promises is "the car
 * is on a road", so the reach has to cover the error the device actually produces.
 *
 * Past it, the driver is genuinely not on a mapped road — a car park, a forecourt, a
 * private drive, a road missing from OSM — and the raw fix is the truthful picture.
 */
export const ROAD_SNAP_METERS = 45

/**
 * How far around the car to look for roads, in SCREEN PIXELS.
 *
 * ⚠️ PIXELS, BECAUSE THAT IS WHAT `queryRenderedFeatures` TAKES, and it means the box is a
 * different size in metres at every zoom. That is fine here and the reason is worth
 * stating: the box only has to be big enough to CONTAIN a road within `ROAD_SNAP_METERS`,
 * and the distance test afterwards is in metres regardless. At the driving zoom (17.5) 140px
 * is roughly 100m, comfortably wider than the 45m threshold — it was 80px against a 25m
 * threshold, and both moved together so the box can never be the thing that hides a road
 * the distance test would have accepted.
 */
const QUERY_BOX_PX = 140

/** Tile geometry arrives as LineString or MultiLineString; flatten both to arrays of paths. */
function pathsOf(feature) {
    const geometry = feature?.geometry
    if (!geometry) return []
    if (geometry.type === 'LineString') return [geometry.coordinates]
    if (geometry.type === 'MultiLineString') return geometry.coordinates
    return []
}

/**
 * The nearest point on a drivable road to `point`, or null.
 *
 * @param {object} map        the Mapbox GL map
 * @param {{lat: number, lng: number}} point  the raw fix
 * @param {string} layerId    the invisible road layer to query
 * @param {number} [maxMetres]
 * @returns {{ok: boolean, reason: ?string, metres: ?number, lat?: number, lng?: number,
 *            bearing?: ?number, roadClass?: ?string, roadName?: ?string}}
 *          ALWAYS an object — `ok` says whether the car may be moved onto it. `bearing` is
 *          the direction of the matched road SEGMENT, which the caller can use to point the
 *          car along the road rather than along its own GPS track. `reason`, `roadClass`
 *          and `roadName` exist so a car drawn off the tarmac can be explained.
 */
export function nearestRoadPoint(map, point, layerId, maxMetres = ROAD_SNAP_METERS) {
    if (!map || !point || !Number.isFinite(point.lat) || !Number.isFinite(point.lng)) {
        return {ok: false, reason: 'no-fix', metres: null}
    }
    /* The layer is added on `load`; a query before that is ordinary startup, not a fault. */
    if (!map.getLayer?.(layerId)) return {ok: false, reason: 'no-layer', metres: null}

    let features
    try {
        const at = map.project([point.lng, point.lat])
        features = map.queryRenderedFeatures(
            [[at.x - QUERY_BOX_PX, at.y - QUERY_BOX_PX], [at.x + QUERY_BOX_PX, at.y + QUERY_BOX_PX]],
            {layers: [layerId]},
        )
    } catch {
        /* `project` throws on a camera mid-teardown, and a query against a style that is
           being replaced can too. A missed match is a frame drawn at the raw fix. */
        return {ok: false, reason: 'query-threw', metres: null}
    }
    if (!features || features.length === 0) return {ok: false, reason: 'no-features', metres: null}

    let best = null

    for (const feature of features) {
        for (const coordinates of pathsOf(feature)) {
            if (!Array.isArray(coordinates) || coordinates.length < 2) continue

            /* ⚠️ SEARCHED FROM 0 WITH NO WINDOW, unlike the route match. The windowing there
               exists to keep progress along ONE line monotonic; these are unrelated roads
               re-fetched every fix, with no previous index that means anything. */
            const path = coordinates.map(([lng, lat]) => ({lat, lng}))
            const found = nearestPointOnPath(point, path)
            if (!found) continue
            if (best && found.metres >= best.metres) continue

            /* The segment the match landed on, for the road's own direction. `index` is the
               vertex at or after the projection, so the segment is the one before it. */
            const b = Math.max(1, Math.min(found.index, path.length - 1))
            const bearing = bearingBetween(path[b - 1], path[b])

            best = {
                lat: found.lat,
                lng: found.lng,
                metres: found.metres,
                bearing: Number.isFinite(bearing) ? bearing : null,
                /* Diagnostics only — never read for a decision. When a car is drawn off the
                   tarmac the first question is always "was there a road in range, and what
                   did the data call it", and without these the answer is a guess. */
                roadClass: feature?.properties?.class || null,
                roadName: feature?.properties?.name || null,
            }
        }
    }

    /* ⚠️ THE REJECTED MATCH IS RETURNED, FLAGGED — not thrown away. "No road within 25m"
       and "no roads in the tiles at all" produce an identical car on screen and have
       opposite causes, and the caller cannot tell them apart from a bare null. `ok` is what
       the caller gates the snap on; everything else is for the console. */
    if (!best) return {ok: false, reason: 'no-roads', metres: null}
    if (best.metres > maxMetres) return {...best, ok: false, reason: 'too-far'}
    return {...best, ok: true, reason: null}
}


/**
 * WHICH WAY ALONG THE ROAD THE CAR IS POINTING.
 *
 * A road segment has a direction in the data, and it is arbitrary — the tile's digitising
 * order, not the direction of travel. Snapping the car's angle to it directly points half
 * the fleet backwards down perfectly ordinary two-way streets.
 *
 * So the road only decides the LINE the car sits on; the driver's own course decides which
 * end of it is forward. Where they disagree by more than a right angle, the segment is
 * flipped.
 *
 * ⚠️ NO COURSE MEANS NO ANSWER, not "use the road's own direction". A car that has not
 * moved yet has nothing to be pointed along, and guessing gives a 50% chance of drawing it
 * facing the traffic.
 */
export function alignToRoad(roadBearing, course) {
    if (!Number.isFinite(roadBearing)) return null
    if (!Number.isFinite(course)) return null

    const delta = Math.abs((((roadBearing - course) % 360) + 540) % 360 - 180)

    /**
     * ⚠️ PERPENDICULAR MEANS "MID-TURN", AND MID-TURN THIS FUNCTION MUST NOT ANSWER.
     *
     * The flip is decided by comparing the road to the driver's recent course, and right
     * after a turn those two disagree by about a right angle: the car is already on the new
     * road while the course still describes the old one. At 90° the comparison is a coin
     * toss, and it lost about half the time — the car snapped to face BACK down the road it
     * had just joined, held there for a fix or two, then swung round once the course caught
     * up. Reported as "on every turn the car rotates opposite first".
     *
     * Returning null hands the decision to the next source in `carHeading` — the driver's
     * own track — which is never reversed, only a little behind. A heading that lags a
     * moment is a car that looks slow to turn; a heading that flips is a car that looks
     * broken.
     */
    if (delta > 60 && delta < 120) return null

    return delta > 90 ? (roadBearing + 180) % 360 : roadBearing
}
